import * as z from "zod";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Loader from "@/components/shared/Loader";
import { useToast } from "@/components/ui/use-toast";
import { useCreateUserAccount } from "@/lib/react-query/queries";
import { SignupValidation } from "@/lib/validation";
import { deleteAllSessions, getExistingUserByPhoneNumber } from "@/lib/appwrite/api";
import BusinessSLA from "./BusinessSLA";


const SignUpForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isBusinessUser, setIsBusinessUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof SignupValidation>>({
    resolver: zodResolver(SignupValidation),
    defaultValues: {
      name: "",
      username: "",
      emailOrPhone: "",
      password: "",
      acceptedSLA: false,
    },
  });

  const { mutateAsync: createUserAccount } = useCreateUserAccount();

  const handleSignUp = async (values: z.infer<typeof SignupValidation>) => {
    if (!acceptedTerms) {
      toast({
        title: "Acceptance Required",
        description: "You must accept our Terms to proceed.",
        variant: "destructive",
      });
      return;
    }
    const existingUser = await getExistingUserByPhoneNumber("+91"+values.emailOrPhone+"@busiconn.com");
    
    if(existingUser.user) {
       toast({
            title: "User Already Exists!",
            description: "Please try logging in.",
          });
          navigate('/sign-in');
          return;
    }
    setIsLoading(true);
    const formIsValid = await form.trigger();
    if (!formIsValid) {
      setIsLoading(false);
      return;
    }
    
    try {
      await deleteAllSessions();
      const result = await createUserAccount({
        ...values,
        acceptedSLA: isBusinessUser,
      });
      
      if (!result) {
        toast({ title: "Sign up failed. Please try again." });
        return;
      }

      const isEmail = values.emailOrPhone.includes("@");

      if (isEmail) {
        toast({
          title: "Verification email sent",
          description: "Please check your email for verification instructions.",
        });

        sessionStorage.setItem("pendingVerification", values.emailOrPhone);
        navigate("/verify-email");
      } else {
        if ("phoneSession" in result) {
          values.acceptedSLA = isBusinessUser;
          sessionStorage.setItem(
            "phoneVerification",
            JSON.stringify({
              user: values,
              userId: result.phoneSession?.userId,
              sessionId: result.phoneSession?.$id,
            })
          );
        }
        navigate("/verify-phone");
      }
    } catch (error) {
      console.error("SignUp Error:", error);
      let errorMessage = "Please try again later";
      if (error instanceof Error) {
        errorMessage = error.message.includes("already exists")
          ? "This email/phone is already registered. Please try logging in instead."
          : error.message;
      }
      toast({
        title: "Sign up failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <div className="flex-center flex-col px-4 py-8 w-full max-w-md mx-auto">
        <img
          className="w-40 mb-6"
          src="/assets/images/busiconn.svg"
          alt="busiconn"
        />

        <h2 className="h3-bold md:h2-bold text-center">
          Create a new account
        </h2>
        <p className="text-light-3 small-medium md:base-regular mt-2 text-center">
          Join busiconn to connect, collaborate, or grow your business
        </p>

        <form
          onSubmit={form.handleSubmit(handleSignUp)}
          className="flex flex-col gap-5 w-full mt-4"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Full Name</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    className="shad-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Username</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Choose a username"
                    className="shad-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="emailOrPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Phone</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Your 10 digit mobile number"
                    className="shad-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      className="shad-input pr-10"
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center space-x-2 mt-2">
            <Checkbox
              id="business-account"
              checked={isBusinessUser}
              onCheckedChange={(checked) => setIsBusinessUser(!!checked)}
              className="h-5 w-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            <label
              htmlFor="business-account"
              className="text-sm font-medium leading-none text-gray-300"
            >
              I'm signing up as a business (to sell or hire)
            </label>
          </div>

          <div className="flex items-start space-x-2 mt-2">
            <Checkbox
              id="terms-agreement"
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(!!checked)}
              className="h-5 w-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500 mt-0.5"
            />
            <label
              htmlFor="terms-agreement"
              className="text-sm font-medium leading-none text-gray-300"
            >
              I agree to the{" "}
              <Link
                to="/termsandconditions"
                className="text-primary-500 underline"
              >
                Terms and Conditions
              </Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="text-primary-500 underline">
                Privacy Policy
              </Link>
              {isBusinessUser && (
                <>
                  {' '}and acknowledge the{' '}
                  <BusinessSLA />
                </>
              )}
            </label>
          </div>

          <Button
            type="submit"
            className="shad-button_primary mt-4 w-full py-6 text-md font-semibold"
            disabled={isLoading}
          >
            {isLoading ? <Loader /> : "Create Account"}
          </Button>

          <p className="text-small-regular text-light-2 text-center mt-4">
            Already have an account?{" "}
            <Link
              to="/sign-in"
              className="text-primary-500 text-small-semibold ml-1"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </Form>
  );
};

export default SignUpForm;